import {
  Home,
  Flame,
  Tv2,
  UploadCloud,
  Layers,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  History,
  ShieldCheck,
  Clapperboard,
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext.js";
import { useAuth } from "../context/AuthContext.js";
import { BrandLogo } from "./common/BrandLogo.js";

export type NavTab = "home" | "shorts" | "trending" | "subscriptions" | "history" | "upload" | "offline" | "arch" | "settings" | "channel" | "player" | "studio" | "search" | "admin" | "clip" | "live_join" | "live_studio";

export interface SidebarProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onTabChange,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen = false,
  onCloseMobile,
}) => {
  const { t } = useI18n();
  const { user, profile, hasChannel, setIsAuthModalOpen, setIsCreateChannelModalOpen } = useAuth();

  const isAdmin = Boolean(
    user && (
      (user as any).role === "ADMIN" ||
      profile?.role === "ADMIN" ||
      user.user_metadata?.role === "ADMIN" ||
      user.app_metadata?.role === "ADMIN"
    )
  );

  const navItems = [
    { id: "home" as NavTab, label: t.nav.home, icon: Home },
    { id: "shorts" as NavTab, label: t.nav.shorts, icon: Clapperboard },
    { id: "trending" as NavTab, label: t.nav.trending, icon: Flame },
    { id: "subscriptions" as NavTab, label: t.nav.subscriptions, icon: Tv2 },
    { id: "history" as NavTab, label: t.nav.history, icon: History },
    { id: "offline" as NavTab, label: t.nav.offlineLibrary, icon: Download },
    { id: "upload" as NavTab, label: t.nav.uploadStudio, icon: UploadCloud },
    ...(isAdmin ? [{ id: "admin" as NavTab, label: t.nav.adminPortal, icon: ShieldCheck }] : []),
    { id: "settings" as NavTab, label: t.nav.settings, icon: Settings },
  ];

  const handleNavClick = (tab: NavTab) => {
    if (tab === "upload") {
      if (!user) {
        setIsAuthModalOpen(true);
        if (onCloseMobile) onCloseMobile();
        return;
      }
      if (!hasChannel) {
        setIsCreateChannelModalOpen(true);
        if (onCloseMobile) onCloseMobile();
        return;
      }
      onTabChange("studio");
      if (onCloseMobile) onCloseMobile();
      return;
    }
    if (tab === "admin" && !isAdmin) {
      if (onCloseMobile) onCloseMobile();
      return;
    }
    onTabChange(tab);
    if (onCloseMobile) onCloseMobile();
  };

  return (
    <>
      {/* 1. Mobile Backdrop Overlay (Visible only on < md when drawer is open) */}
      {isMobileOpen && (
        <div
          onClick={onCloseMobile}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden animate-in fade-in duration-200"
        />
      )}

      {/* 2. Responsive Sidebar Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 md:top-16 md:bottom-0 md:z-30 bg-zinc-950/95 backdrop-blur-xl border-r border-zinc-800/80 transition-all duration-300 flex flex-col font-sans select-none ${
          isMobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"
        } ${isCollapsed ? "md:w-16" : "w-64 md:w-60"}`}
      >
        {/* Mobile Drawer Header with Close Button (< md only) */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800/80 md:hidden">
          <BrandLogo variant="full" size="sm" onClick={onCloseMobile} />
          <button
            onClick={onCloseMobile}
            className="p-1.5 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            title="Close Sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items List */}
        <div className="flex-1 py-4 px-2 space-y-1.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-semibold transition-all group relative ${
                  isActive
                    ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shadow-md shadow-emerald-500/5"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900/80"
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon
                  className={`w-5 h-5 shrink-0 transition-colors ${
                    isActive ? "text-emerald-400" : "text-zinc-400 group-hover:text-white"
                  }`}
                />
                {/* Text label: Always visible on mobile or when expanded on desktop */}
                <span className={`truncate ${isCollapsed ? "md:hidden" : ""}`}>
                  {item.label}
                </span>

                {/* Tooltip on collapsed hover (Desktop only) */}
                {isCollapsed && (
                  <div className="hidden md:block absolute left-16 bg-zinc-900 text-white text-xs font-semibold px-2.5 py-1 rounded-md shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap border border-zinc-800">
                    {item.label}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Desktop Collapse Toggle Footer (Hidden on mobile) */}
        <div className="hidden md:block p-2 border-t border-zinc-800/80">
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center p-2.5 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 text-zinc-400 hover:text-white transition-colors"
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* 3. Fixed Mobile Bottom Navigation Bar (Home, Shorts, Trending, Subscriptions, History) */}
      <nav className="fixed bottom-0 inset-x-0 h-14 bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800/80 z-40 md:hidden flex items-center justify-around px-2 font-sans select-none shadow-2xl">
        {[
          { id: "home" as NavTab, label: t.nav.home, icon: Home },
          { id: "shorts" as NavTab, label: t.nav.shorts, icon: Clapperboard },
          { id: "trending" as NavTab, label: t.nav.trending, icon: Flame },
          { id: "subscriptions" as NavTab, label: t.nav.subscriptions, icon: Tv2 },
          { id: "history" as NavTab, label: t.nav.history, icon: History },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`flex flex-col items-center justify-center flex-1 py-1 transition-all ${
                isActive ? "text-emerald-400 font-bold" : "text-zinc-400 hover:text-white"
              }`}
            >
              <Icon className={`w-5 h-5 transition-transform ${isActive ? "text-emerald-400 scale-110" : "text-zinc-400"}`} />
              <span className="text-[10px] mt-0.5 tracking-tight truncate max-w-[70px]">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};
